import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as variazioniApi from "api/personale/variazioniDisponibilitaApi";
import type { VariazioniFetchParams } from "api/personale/variazioniDisponibilitaApi";

// ============================================
// INTERFACES
// ============================================

export interface VariazioneDisponibilita {
    id?: number;
    operatore?: number | null;
    operatore_display?: string | null;
    reparto: number;
    reparto_display?: string;
    reparto_origine_id?: number | null;  // read-only, derivato dal backend
    data_da: string;                     // YYYY-MM-DD
    data_a: string;                      // YYYY-MM-DD
    ore_giornaliere: number;             // segno libero
    note?: string | null;
    created_at?: string;
    updated_at?: string;
}

const rangeKey = (dataDa: string, dataA: string) => `${dataDa}_${dataA}`;

// ============================================
// STATO SLICE
// ============================================

export interface VariazioniDisponibilitaState {
    byRange: { [key: string]: VariazioneDisponibilita[] };
    ultimoRange?: string;                 // chiave dell'ultimo fetch — usata per refresh post-save
    rangeErrorsStack: ErrorsStack;
    saveErrorsStack: ErrorsStack;
    deleteErrorsStack: ErrorsStack;
}

const initialState: VariazioniDisponibilitaState = {
    byRange: {},
    ultimoRange: undefined,
    rangeErrorsStack: { status: ErrorStatusTypes.OK },
    saveErrorsStack: { status: ErrorStatusTypes.OK },
    deleteErrorsStack: { status: ErrorStatusTypes.OK },
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchVariazioni = createAsyncThunk(
    "variazioniDisponibilita/fetchVariazioni",
    async (params: VariazioniFetchParams) => {
        const response = await variazioniApi.fetchVariazioni(params);
        return {
            range: rangeKey(params.dataDa, params.dataA),
            results: (response.results || []) as VariazioneDisponibilita[],
        };
    }
);

export const saveVariazione = createAsyncThunk(
    "variazioniDisponibilita/saveVariazione",
    async (variazione: VariazioneDisponibilita) => {
        if (variazione.id) {
            return await variazioniApi.updateVariazione(variazione);
        }
        return await variazioniApi.createVariazione(variazione);
    }
);

/**
 * PATCH dei soli campi cambiati (drag/resize sul Gantt). Evita di mandare un payload
 * pieno con valori segnaposto (es. `reparto: 0`) che il backend rifiuterebbe.
 */
export const updateVariazionePartial = createAsyncThunk(
    "variazioniDisponibilita/updateVariazionePartial",
    async (args: { id: number; patch: Partial<VariazioneDisponibilita> }) => {
        return await variazioniApi.updateVariazionePartial(args.id, args.patch);
    }
);

export const removeVariazione = createAsyncThunk(
    "variazioniDisponibilita/removeVariazione",
    async (id: number) => {
        await variazioniApi.deleteVariazione(id);
        return id;
    }
);

// ============================================
// SLICE
// ============================================

function upsertNelRange(list: VariazioneDisponibilita[], v: VariazioneDisponibilita) {
    const idx = list.findIndex((x) => x.id === v.id);
    if (idx >= 0) {
        list[idx] = v;
    } else {
        list.push(v);
    }
}

export const variazioniDisponibilitaSlice = createSlice({
    name: "variazioniDisponibilita",
    initialState,
    reducers: {
        resetVariazioni: (state) => {
            state.byRange = {};
            state.ultimoRange = undefined;
            state.rangeErrorsStack = { status: ErrorStatusTypes.OK };
            state.saveErrorsStack = { status: ErrorStatusTypes.OK };
            state.deleteErrorsStack = { status: ErrorStatusTypes.OK };
        },
    },
    extraReducers: (builder) => {
        // --- fetch ---
        builder.addCase(fetchVariazioni.pending, (state) => {
            state.rangeErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchVariazioni.fulfilled, (state, action) => {
            const { range, results } = action.payload;
            state.byRange[range] = results;
            state.ultimoRange = range;
            state.rangeErrorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchVariazioni.rejected, (state, action) => {
            state.rangeErrorsStack = parseErrorMessage(action.error);
        });

        // --- save ---
        builder.addCase(saveVariazione.pending, (state) => {
            state.saveErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(saveVariazione.fulfilled, (state, action) => {
            const saved = action.payload as VariazioneDisponibilita;
            if (state.ultimoRange) {
                const list = state.byRange[state.ultimoRange] || [];
                upsertNelRange(list, saved);
                state.byRange[state.ultimoRange] = list;
            }
            state.saveErrorsStack = { status: ErrorStatusTypes.SUCCESS };
        });
        builder.addCase(saveVariazione.rejected, (state, action) => {
            state.saveErrorsStack = parseErrorMessage(action.error);
        });

        // --- update partial (drag/resize) ---
        builder.addCase(updateVariazionePartial.pending, (state) => {
            state.saveErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(updateVariazionePartial.fulfilled, (state, action) => {
            const saved = action.payload as VariazioneDisponibilita;
            if (state.ultimoRange) {
                const list = state.byRange[state.ultimoRange] || [];
                upsertNelRange(list, saved);
                state.byRange[state.ultimoRange] = list;
            }
            state.saveErrorsStack = { status: ErrorStatusTypes.SUCCESS };
        });
        builder.addCase(updateVariazionePartial.rejected, (state, action) => {
            state.saveErrorsStack = parseErrorMessage(action.error);
        });

        // --- delete ---
        builder.addCase(removeVariazione.pending, (state) => {
            state.deleteErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(removeVariazione.fulfilled, (state, action) => {
            const id = action.payload;
            Object.keys(state.byRange).forEach((k) => {
                state.byRange[k] = state.byRange[k].filter((x) => x.id !== id);
            });
            state.deleteErrorsStack = { status: ErrorStatusTypes.SUCCESS };
        });
        builder.addCase(removeVariazione.rejected, (state, action) => {
            state.deleteErrorsStack = parseErrorMessage(action.error);
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const { resetVariazioni } = variazioniDisponibilitaSlice.actions;

export const variazioniDisponibilitaSliceReducer = variazioniDisponibilitaSlice.reducer;
